/**
 * The one rule for "did this change cause this failure?" on the one-attempt
 * lane. A failed check's run on the change and its run on the unchanged code
 * (the base) are each read as failure keys, and the change caused what the
 * base did not already have:
 *
 *  - keys are a multiset and name no line (`test:<name>`, `diag:<file>\x1f<message>`,
 *    `ann:<path>\x1f<message>`), so a failure a change moved is the same failure,
 *    and a second copy of a failure the base had once is a new one;
 *  - the step must match when both runs name one;
 *  - a failure that names nothing (output no parser reads) is never excused;
 *  - with no run on the base there is nothing to compare, and the failure counts.
 *
 * Its callers: the lane's red CI (`codeReview/prSignals/baseFailures.ts`,
 * GitHub's runs), and the session's verify gate in pi-extension-yagni, which
 * does not depend on this package and keeps the region between the markers
 * below byte for byte in its own `src/pipeline/checkAttribution.ts`. Its test
 * (`test/pipeline/checkAttribution.test.ts`) fails when the two differ, and
 * both packages' tests read one corpus,
 * `pi-extension-yagni/test/fixtures/checkAttribution.json`.
 *
 * The session measures each check on the base once per attempt and base
 * commit, and writes what it measured to `base-measurements.json` beside its
 * `result.json`; the host keeps it as a `base_measured` attempt event and
 * hands it to the next session in `checks.json`, which then measures only
 * what is missing.
 */
// --- mirrored region: begin (pi-extension-yagni/src/pipeline/checkAttribution.ts)
/** Package-manager words that are the manager's own command, never a script. */
const YARN_COMMANDS = new Set("add audit autoclean bin cache check config constraints create dedupe dlx exec explain generate-lock-entry global help import info init install licenses link list login logout node npm outdated owner pack patch patch-commit plugin policies publish rebuild remove run set stage tag team unlink unplug up upgrade upgrade-interactive version versions why workspace workspaces".split(" "));
const PNPM_COMMANDS = new Set("add audit bin cache cat-file cat-index config create dedupe deploy dlx doctor env exec fetch find-hash i import init install install-test it licenses link list ln ls outdated pack patch patch-commit patch-remove prune publish rb rebuild recursive remove rm root run self-update server setup store un uninstall unlink up update why".split(" "));
/** The scripts npm runs without `run`: its lifecycle shorthands. */
const NPM_SCRIPT_SHORTHANDS = new Set(["test", "start", "stop", "restart"]);
/**
 * A command as two lists compare it: the same words, whatever spaces sit
 * between them, and a package manager's script named the one way it runs,
 * so `yarn test` and `yarn run test`, `npm test` and `npm run test`, `pnpm
 * lint` and `pnpm run lint` are one check. Only the leading form: a
 * manager's flags before the script leave the command as it is. Both
 * packages' tests read one corpus,
 * pi-extension-yagni/test/fixtures/equivalentCheckCommands.json.
 */
export function canonicalCheckCommand(command) {
    const words = command.trim().split(/\s+/).filter(Boolean);
    const [manager, next, ...rest] = words;
    const script = /^[A-Za-z0-9][\w:.@/-]*$/;
    if (manager === "npm" || manager === "yarn" || manager === "pnpm") {
        const runs = next === "run" || (manager === "npm" && next === "run-script");
        if (runs && rest[0] && script.test(rest[0]))
            return [manager, "run", ...rest].join(" ");
        const shorthand = next !== undefined &&
            script.test(next) &&
            (manager === "npm"
                ? NPM_SCRIPT_SHORTHANDS.has(next)
                : !(manager === "yarn" ? YARN_COMMANDS : PNPM_COMMANDS).has(next));
        if (shorthand)
            return [manager, "run", next, ...rest].join(" ");
    }
    return words.join(" ");
}
/**
 * What a check id and a located failure key hold between their two parts
 * (the folder and the command, the file and the message): the ASCII unit
 * separator, which no path, command or message carries. Never NUL: the keys
 * and ids are kept in Postgres JSONB (a `base_measured` event), which refuses
 * a string holding \u0000.
 */
export const CHECK_KEY_SEPARATOR = "\u001f";
/**
 * One check's identity across the change's run and the base's, and across
 * sessions: the folder it runs in, relative to the repository root, and its
 * command as {@link canonicalCheckCommand} reads it.
 */
export function checkId(command, cwd) {
    const dir = cwd
        .replace(/\\/g, "/")
        .replace(/^(?:\.\/+)+/, "")
        .replace(/\/+$/, "") || ".";
    return `${dir}${CHECK_KEY_SEPARATOR}${canonicalCheckCommand(command)}`;
}
/** A failing test's key: its name, without the duration a runner prints after it. */
export function testKey(name) {
    return `test:${name.replace(/\s*\(\d+(?:\.\d+)?\s*m?s\)\s*$/, "").trim()}`;
}
/** A located diagnostic's key: its file and message, never its line, which a change above it moves. */
export function diagKey(file, message) {
    return `diag:${file.replace(/\\/g, "/").replace(/^(?:\.\/+)+/, "")}${CHECK_KEY_SEPARATOR}${message.trim()}`;
}
/** A check annotation's key: its path and message, never its line. */
export function annotationKey(path, message) {
    return `ann:${path}${CHECK_KEY_SEPARATOR}${message.trim()}`;
}
/**
 * The rule. `excused` is index for index with `head.keys`: true where the
 * base already had that failure, each base key excusing one head key at most.
 * Pure.
 */
export function attributeFailure(head, base) {
    const excused = head.keys.map(() => false);
    if (!base)
        return { verdict: "no_baseline", excused };
    if (head.step && base.step && head.step !== base.step)
        return { verdict: "caused", excused };
    const left = new Map();
    for (const key of base.keys)
        if (key !== null)
            left.set(key, (left.get(key) ?? 0) + 1);
    head.keys.forEach((key, index) => {
        const count = key === null ? 0 : (left.get(key) ?? 0);
        if (key === null || count === 0)
            return;
        left.set(key, count - 1);
        excused[index] = true;
    });
    const count = excused.filter(Boolean).length;
    const verdict = count === 0 ? "caused" : count === head.keys.length ? "preexisting" : "partly";
    return { verdict, excused };
}
/** The file the session writes beside its `result.json`, and the bounds both sides keep. */
export const BASE_MEASUREMENTS_FILE_NAME = "base-measurements.json";
export const MAX_BASE_MEASUREMENTS = 100;
export const MAX_BASE_KEYS = 1_000;
export const MAX_BASE_KEY_CHARS = 500;
/**
 * One measurement within the bounds: a key longer than the bound is left
 * out, and keys past the count bound are dropped. Either only makes fewer
 * failures excused, never more.
 */
export function boundedMeasurement(measurement) {
    return {
        id: measurement.id,
        outcome: measurement.outcome,
        keys: measurement.outcome === "passed"
            ? []
            : measurement.keys
                .filter((key) => key.length <= MAX_BASE_KEY_CHARS)
                .slice(0, MAX_BASE_KEYS),
    };
}
// --- mirrored region: end
/** A failing-test bullet: jest `✕ name`, vitest `×/✗ name`, node spec `✖ name`. */
const FAIL_BULLET = /^[✕✖✗×]\s+(.+)$/;
/**
 * A bullet that names no test: node:test's footer header, and ESLint's
 * `✖ 3 problems (3 errors, 0 warnings)`, whose rows are diagnostics.
 */
const NOT_A_TEST = /^(?:failing tests:?|\d+ problems? \(.*\))$/i;
/** TAP: `not ok 1 - name`. */
const TAP_NOT_OK = /^not ok\s+\d+\s+-\s+(.+)$/;
/** vitest: ` FAIL  file > suite > name`. */
const RUNNER_FAIL = /^FAIL\s+(\S+)(?:\s+>\s+(.+))?/;
/**
 * Other runners' failing-test lines, each naming its test: pytest's
 * `FAILED file::name` and Pest's `FAILED Class > name` (without pytest's
 * ` - reason`; a bare `FAILED to start` is no test), PHPUnit's
 * `1) Class::method`, go's `--- FAIL: TestName`.
 */
const NAMED_TEST_FAILURE = [
    /^FAILED\s+(\S+(?:::|\s+>\s+)\S.*?)(?:\s+-\s.*)?$/,
    /^\d+\)\s+(\S+::\S+)(?:\s+with data set .*)?$/,
    /^--- FAIL:\s+(\S+)/,
];
/** A test run that failed without a line naming its tests. */
const FAILURE_SUMMARY = [
    /(?:^|\n)\s*# fail\s+[1-9]\d*\b/,
    /\bTests:\s+(?:\d+\s+\w+,\s*)*[1-9]\d*\s+failed\b/,
    /\bTests\s+[1-9]\d*\s+failed\b/,
    /\b[1-9]\d*\s+failing\b/,
    /[✕✖✗×]\s+failing tests:/u,
];
/** TypeScript: `path(line,col): error TSxxxx: message`. */
const TS_DIAGNOSTIC = /^(.+?)\((\d+),\d+\):\s*error\s+TS\d+:\s*(.+)$/;
/** eslint and friends: `path:line:col: message`. */
const GENERIC_DIAGNOSTIC = /^(.+?):(\d+):\d+:?\s*(?:error\b[:\s]*)?(.+)$/;
/** ESLint stylish: a file on its own line, then `line:col  error  message  rule` rows. */
const STYLISH_FILE = /^(?:\.{0,2}\/)?[^\s:]+\.[A-Za-z0-9]+$/;
const STYLISH_ERROR = /^(\d+):\d+\s+(?:error|Error:)\s+(.+)$/;
const ANSI = /\u001b\[[0-9;]*[A-Za-z]/g;
/**
 * What a failed check's output says failed, as keys: the check's failure
 * annotations when it has any; else its failing tests (each name once); else,
 * when a test runner's summary says tests failed without naming them,
 * nothing; else its located diagnostics (each `file:line:message` once, the
 * line then dropped). `[]` when the output names nothing it can read. The
 * same reading as the session's gate (pi-extension-yagni's
 * `classifyTestFailure`, then `parseVerifyFailures`), which the corpus holds.
 */
export function failureKeys(output, opts = {}) {
    const annotations = (opts.annotations ?? []).filter((annotation) => annotation.level === undefined || annotation.level === "failure");
    if (annotations.length > 0) {
        return annotations.map((annotation) => annotationKey(annotation.path, annotation.message));
    }
    const lines = output
        .replace(ANSI, "")
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);
    const tests = testKeysOf(lines);
    if (tests.length > 0)
        return tests;
    if (FAILURE_SUMMARY.some((summary) => summary.test(output)))
        return [];
    return diagnosticKeysOf(lines);
}
/** The test name one line gives, or "" when it names none. */
function testNameOfLine(line) {
    const bullet = line.match(FAIL_BULLET) ?? line.match(TAP_NOT_OK);
    if (bullet)
        return bullet[1];
    const fail = line.match(RUNNER_FAIL);
    if (fail && /\.[cm]?[jt]sx?$/.test(fail[1]))
        return fail[2] ?? "";
    return NAMED_TEST_FAILURE.map((re) => line.match(re)).find(Boolean)?.[1] ?? "";
}
/** One line's failing test, as a key, or null when it names none. */
function testKeyOfLine(line) {
    const key = testKey(testNameOfLine(line));
    const name = key.slice("test:".length);
    return name && name.length <= 200 && !NOT_A_TEST.test(name) ? key : null;
}
/** The failing tests the lines name, each once. */
function testKeysOf(lines) {
    const tests = new Set();
    for (const line of lines) {
        const key = testKeyOfLine(line);
        if (key)
            tests.add(key);
    }
    return [...tests];
}
/** One line's located diagnostic, under the stylish file header read before it. */
function diagnosticOf(line, stylishFile) {
    const match = line.match(TS_DIAGNOSTIC) ?? line.match(GENERIC_DIAGNOSTIC);
    if (match)
        return { file: match[1].trim(), line: match[2], message: match[3].trim() };
    const row = stylishFile ? line.match(STYLISH_ERROR) : null;
    if (!row || !stylishFile)
        return null;
    return { file: stylishFile, line: row[1], message: row[2].replace(/\s{2,}/g, " ").trim() };
}
/** The located diagnostics the lines name, each `file:line:message` once, the line then dropped. */
function diagnosticKeysOf(lines) {
    const keys = [];
    const seen = new Set();
    let stylishFile;
    for (const line of lines) {
        const found = diagnosticOf(line, stylishFile);
        if (!found) {
            if (STYLISH_FILE.test(line))
                stylishFile = line;
            continue;
        }
        const { file, message } = found;
        if (!file || /\s/.test(file) || !Number.isFinite(Number(found.line)))
            continue;
        const located = `${file}:${found.line}:${message}`;
        if (seen.has(located))
            continue;
        seen.add(located);
        keys.push(diagKey(file, message));
    }
    return keys;
}
/** A string with every NUL read as {@link CHECK_KEY_SEPARATOR}, what an older session wrote in its place. */
function withoutNul(text) {
    return text.replace(/\u0000/g, CHECK_KEY_SEPARATOR);
}
const GIT_SHA = /^[0-9a-f]{40}([0-9a-f]{24})?$/;
/**
 * Read `base-measurements.json` (the parsed object or its text), or a
 * `base_measured` event's payload, which has the same shape. Null when it is
 * not one; a malformed check is dropped rather than trusted, and every check
 * is held to the bounds. A session built before {@link CHECK_KEY_SEPARATOR}
 * wrote NUL between a key's or an id's parts; it is read as the separator, so
 * the record never holds a NUL (Postgres JSONB refuses one). A session built
 * since matches it by id; an older one finds no match and measures again.
 */
export function parseBaseMeasurements(input) {
    let raw = input;
    if (typeof input === "string") {
        try {
            raw = JSON.parse(input);
        }
        catch {
            return null;
        }
    }
    if (!raw || typeof raw !== "object" || Array.isArray(raw))
        return null;
    const body = raw;
    if (typeof body.baseSha !== "string" || !GIT_SHA.test(body.baseSha))
        return null;
    if (!Array.isArray(body.checks))
        return null;
    const checks = [];
    for (const entry of body.checks.slice(0, MAX_BASE_MEASUREMENTS)) {
        if (!entry || typeof entry !== "object")
            continue;
        const check = entry;
        if (typeof check.id !== "string" || !check.id)
            continue;
        const id = withoutNul(check.id);
        if (check.outcome !== "passed" && check.outcome !== "failed")
            continue;
        const keys = Array.isArray(check.keys)
            ? check.keys.filter((key) => typeof key === "string").map(withoutNul)
            : [];
        checks.push(boundedMeasurement({ id, outcome: check.outcome, keys }));
    }
    return { baseSha: body.baseSha, checks };
}
/**
 * What every session of an attempt measured on `baseSha`, one entry per
 * check, a later measurement standing over an earlier one. Measurements on
 * another base are left out: the attempt's base moved.
 */
export function mergeBaseMeasurements(baseSha, measured) {
    if (!baseSha)
        return null;
    const byId = new Map();
    for (const one of measured) {
        if (one?.baseSha !== baseSha)
            continue;
        for (const check of one.checks)
            byId.set(check.id, check);
    }
    return byId.size > 0
        ? { baseSha, checks: [...byId.values()].slice(0, MAX_BASE_MEASUREMENTS) }
        : null;
}
//# sourceMappingURL=checkAttribution.js.map